<template>
  <v-container fluid class="order-home-page pa-0">
    <div class="order-hero">
      <div>
        <div class="order-title">Orders</div>
        <div class="order-subtitle">
          Create orders, view order list, or share remote ordering links.
        </div>
      </div>
    </div>

    <v-row dense class="mt-4">
      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="order-option-card" to="/order/list">
          <v-card-text>
            <div class="option-icon">
              <v-icon color="primary">mdi-format-list-bulleted</v-icon>
            </div>

            <div class="option-title">Order List</div>
            <div class="option-subtitle">
              View all orders, open details, edit, and convert orders to
              invoices.
            </div>

            <v-btn
              class="mt-4"
              small
              rounded
              color="primary"
              dark
              to="/order/list"
            >
              Open List
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="order-option-card" to="/order/create">
          <v-card-text>
            <div class="option-icon">
              <v-icon color="primary">mdi-phone-in-talk-outline</v-icon>
            </div>

            <div class="option-title">In-House Order</div>
            <div class="option-subtitle">
              Use this when you are taking an order by phone or entering it
              yourself.
            </div>

            <v-btn
              class="mt-4"
              small
              rounded
              color="primary"
              dark
              to="/order/create"
              :disabled="!checkRightStatus(72)"
            >
              New Order
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="order-option-card" @click="openCustomerViewDialog">
          <v-card-text>
            <div class="option-icon"><v-icon color="primary">mdi-account-search-outline</v-icon></div>
            <div class="option-title">Customer View</div>
            <div class="option-subtitle">Open a login customer’s dashboard to review invoices and place orders for their locations.</div>
            <v-btn class="mt-4" small rounded color="primary" dark @click.stop="openCustomerViewDialog">Open Customer</v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card
          outlined
          class="order-option-card"
          @click="$router.push('/order/catalog/create')"
        >
          <v-card-text>
            <div class="option-icon">
              <v-icon color="primary">mdi-tablet-cellphone</v-icon>
            </div>

            <div class="option-title">Sales Rep Catalog</div>
            <div class="option-subtitle">
              Use this when a sales rep is with the customer using a tablet.
            </div>

            <v-btn
              class="mt-4"
              small
              rounded
              color="primary"
              dark
              @click.stop="$router.push('/order/catalog/create')"
            >
              Start Catalog
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card
          outlined
          class="order-option-card"
          @click="handleShareOrderLink"
        >
          <v-card-text>
            <div class="option-icon">
              <v-icon color="primary">mdi-share-variant</v-icon>
            </div>

            <div class="option-title">Share Remote Link</div>
            <div class="option-subtitle">
              Share a public order link so customers can place orders
              themselves.
            </div>

            <v-btn class="mt-4" small rounded color="primary" dark>
              Share Link
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="order-option-card" @click="handleCopyOrderLink">
          <v-card-text>
            <div class="option-icon">
              <v-icon color="primary">mdi-link</v-icon>
            </div>

            <div class="option-title">Copy Remote Link</div>
            <div class="option-subtitle">
              Copy the public order link and send it by text, email, or
              WhatsApp.
            </div>

            <v-btn class="mt-4" small rounded color="primary" dark>
              Copy Link
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <SelectCollectionDialog
      v-model="showCollectionDialog"
      :collections="Collections"
      @select="onCollectionSelected"
    />

    <v-dialog v-model="customerViewDialog" max-width="600">
      <v-card class="rounded-xl">
        <v-card-title>Open Customer View</v-card-title>
        <v-card-subtitle>Select an online login customer. Staff access does not send an OTP.</v-card-subtitle>
        <v-card-text>
          <v-autocomplete
            v-model="selectedLoginId"
            :items="loginCustomers"
            :loading="loadingLogins"
            item-value="id"
            outlined
            clearable
            label="Login customer"
            :filter="filterLoginCustomer"
          >
            <template v-slot:selection="{ item }">{{ loginLabel(item) }}</template>
            <template v-slot:item="{ item }"><v-list-item-content><v-list-item-title>{{ loginLabel(item) }}</v-list-item-title><v-list-item-subtitle>{{ formatPhone(item.phoneNumber) }} · {{ (item.Customers || []).length }} locations</v-list-item-subtitle></v-list-item-content></template>
          </v-autocomplete>
          <v-alert v-if="selectedLogin && !(selectedLogin.Customers || []).length" dense text type="warning">Assign at least one location to this login customer first.</v-alert>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="customerViewDialog=false">Cancel</v-btn><v-btn color="primary" :loading="openingCustomerView" :disabled="!canOpenCustomerView" @click="openCustomerView">Open Customer View</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import Hashids from "hashids";
import collectionService from "@/modules/Product/Collection/service.js";
import backofficeService from "@/api/backoffice";
import SelectCollectionDialog from "@/modules/Order/components/OldPublicCatalog/SelectCollectionDialog.vue";

const hashids = new Hashids();

export default {
  name: "order-home",

  components: {
    SelectCollectionDialog,
  },

  data() {
    return {
      showCollectionDialog: false,
      selectedCollection: null,
      pendingAction: null,
      Collections: [],
      customerViewDialog: false,
      loginCustomers: [],
      selectedLoginId: null,
      loadingLogins: false,
      openingCustomerView: false,
    };
  },

  computed: {
    ...mapGetters("global", ["checkRightStatus", "currentBusiness"]),
    selectedLogin() { return this.loginCustomers.find(row => Number(row.id) === Number(this.selectedLoginId)) || null; },
    canOpenCustomerView() { return !!(this.selectedLogin && !this.selectedLogin.isBlacklisted && (this.selectedLogin.Customers || []).length); },
  },

  created() {
    document.title = "smplsale - Orders";

    if (
      this.currentBusiness &&
      this.currentBusiness.metadata &&
      this.currentBusiness.metadata.forceCollection
    ) {
      this.getCollection();
    }
  },

  methods: {
    async openCustomerViewDialog() {
      this.customerViewDialog = true;
      this.selectedLoginId = null;
      this.loadingLogins = true;
      try {
        const response = await backofficeService.Api().get("publicOrderLogin", { params: { limit: 1000, offset: 0 } });
        this.loginCustomers = response.data.data || [];
      } catch (error) {
        this.$swal("Customer View", "Unable to load online login customers.", "error");
      } finally {
        this.loadingLogins = false;
      }
    },
    loginLabel(item) { return [item.name || "Unknown", item.businessName].filter(Boolean).join(" · "); },
    formatPhone(value) { const digits = String(value || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, ""); return digits.length === 10 ? `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}` : value; },
    filterLoginCustomer(item, queryText) { const query = String(queryText || "").toLowerCase().replace(/\D/g, ""); const text = `${item.name || ""} ${item.businessName || ""} ${item.phoneNumber || ""}`.toLowerCase(); return text.includes(String(queryText || "").toLowerCase()) || (!!query && text.replace(/\D/g, "").includes(query)); },
    async openCustomerView() {
      if (!this.canOpenCustomerView) return;
      this.openingCustomerView = true;
      try {
        const response = await backofficeService.Api().post(`publicOrderLogin/${this.selectedLogin.id}/staff-session`);
        const session = response.data.data;
        const code = String(this.currentBusiness.code);
        sessionStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderSession_${code}`, session.verificationToken);
        sessionStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderStaffSession_${code}`, JSON.stringify(session));
        this.$router.push({ path: `/p/o/${hashids.encode(this.currentBusiness.code)}`, query: { staffView: "1" } });
      } catch (error) {
        const data = error && error.data;
        this.$swal("Customer View", (data && data.message) || "Unable to open this customer.", "error");
      } finally {
        this.openingCustomerView = false;
      }
    },
    async getCollection() {
      try {
        const result = await collectionService.getAll({
          remoteOrder: true,
        });

        const collections = result.data || [];

        this.Collections = [
          { id: null, name: "Show All" },
          ...collections.sort((a, b) => {
            return String(a.name || "").localeCompare(String(b.name || ""));
          }),
        ];
      } catch (error) {
        console.log("error getting collection", error);
        this.Collections = [{ id: null, name: "Show All" }];
      }
    },

    shouldSelectCollection() {
      return (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.forceCollection &&
        !this.selectedCollection
      );
    },

    handleShareOrderLink() {
      if (this.shouldSelectCollection()) {
        this.pendingAction = "share";
        this.showCollectionDialog = true;
        return;
      }

      this.shareOrderLink();
    },

    handleCopyOrderLink() {
      if (this.shouldSelectCollection()) {
        this.pendingAction = "copy";
        this.showCollectionDialog = true;
        return;
      }

      this.createOrderLink();
    },

    onCollectionSelected(collection) {
      this.selectedCollection = collection;

      if (this.pendingAction === "share") {
        this.pendingAction = null;
        this.shareOrderLink();
        return;
      }

      if (this.pendingAction === "copy") {
        this.pendingAction = null;
        this.createOrderLink();
      }
    },

    buildOrderLink() {
      const hashId = hashids.encode(this.currentBusiness.code);

      let baseUrl =
        process.env.NODE_ENV === "development"
          ? `http://localhost:8082/p/o/${hashId}`
          : `https://app.smplsale.com/p/o/${hashId}`;

      if (this.selectedCollection && this.selectedCollection.id) {
        baseUrl += `?collectionId=${this.selectedCollection.id}`;
      }

      return baseUrl;
    },

    createOrderLink() {
      const link = this.buildOrderLink();
      this.copyCode(link);
    },

    shareOrderLink() {
      const link = this.buildOrderLink();

      if (navigator.share) {
        navigator
          .share({
            title: `Order Link from ${this.currentBusiness.name}`,
            text: `Here is your order link from ${this.currentBusiness.name}:`,
            url: link,
          })
          .catch(() => {
            this.copyCode(link);
          })
          .finally(() => {
            this.selectedCollection = null;
          });

        return;
      }

      this.copyCode(link);
    },

    copyCode(code) {
      navigator.clipboard.writeText(code);

      this.selectedCollection = null;

      this.$swal
        .mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: true,
          timer: 2000,
        })
        .fire({
          icon: "success",
          title: "Order Link is copied",
        });
    },
  },
};
</script>

<style scoped>
.order-home-page {
  background: #f8fafc;
  min-height: 100%;
}

.order-hero {
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: #ffffff;
  padding: 18px;
}

.order-title {
  font-size: 26px;
  font-weight: 900;
  color: #0f172a;
}

.order-subtitle {
  font-size: 14px;
  color: #64748b;
  margin-top: 3px;
}

.order-option-card {
  border-radius: 20px;
  background: #ffffff;
  height: 100%;
  cursor: pointer;
  transition: 0.15s ease;
}

.order-option-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.option-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.option-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
}

.option-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 6px;
  min-height: 54px;
}

@media (max-width: 600px) {
  .order-title {
    font-size: 22px;
  }

  .option-subtitle {
    min-height: auto;
  }
}
</style>
